import React, { useState, useCallback, useMemo, useContext, useRef } from "react";
import {
  BackLink,
  Title,
  Text,
  Button,
  Autocomplete,
  Field,
  DateTimePickerV2,
  InnerLabelTextfield,
} from "components/commons";
import Skeleton from "@material-ui/lab/Skeleton";
import { ConfirmModal, SuccessModal } from "components/modals";
import { useModal, useRouter, useApi, useMount } from "hooks";
import locale from "localization";
import { Path } from "enums";
import UserContext from "contexts/user.context";
import { getMovement, updateMovement } from "apis";
import { prettifyProductCode, prettifyProductCodeStyle } from "utils/pretty.utils";
import styles from "./price-movement-edit.module.scss";

const PRODUCTS = [
  { code: "gas105" },
  { code: "gas97" },
  { code: "gas95" },
  { code: "gas91" },
  { code: "diesel" },
  { code: "kerosene" },
  { code: "lpg" },
];

const MOVEMENT_TYPE_OPTIONS = [
  { label: "Rollback", value: "ROLLBACK" },
  { label: "Increase", value: "INCREASE" },
  { label: "No Movement", value: "NO_MOVEMENT" },
];

const AMOUNT_MAX = 999.99;

const isNumericPeriod = (val) => {
  if (val === "" || val === undefined) return true;
  return /^[0-9]*\.?[0-9]{0,2}$/.test(val);
};

const fmt2 = (val) => (parseFloat(val) || 0).toFixed(2);

const inferMovementType = (amount) => {
  if (amount > 0) return "INCREASE";
  if (amount < 0) return "ROLLBACK";
  return "NO_MOVEMENT";
};

const ProductDot = ({ code }) => (
  <div
    style={{
      ...prettifyProductCodeStyle(code),
      borderRadius: "50%",
      width: "12px",
      height: "12px",
    }}
  />
);

const PriceMovementEditModule = () => {
  const { history, location } = useRouter();
  const { user } = useContext(UserContext);
  const confirmModal = useModal();
  const successModal = useModal();

  const movementId = location?.state?.movementId;

  const [loading, setLoading] = useState(true);
  const [effectiveDateTime, setEffectiveDateTime] = useState(null);
  const [productRows, setProductRows] = useState(
    PRODUCTS.map((p) => ({ productCode: p.code, movementType: null, amount: "0.00" }))
  );
  const [isSaving, setIsSaving] = useState(false);
  const initialDataRef = useRef(null);

  const { request: fetchMovement, result: movement } = useApi({
    api: getMovement,
    handleOwnError: true,
  });
  const { request: submitUpdate } = useApi({ api: updateMovement, handleOwnError: true });

  const processedBy = user?.fullName || user?.name || user?.email || "";

  useMount(async () => {
    if (!movementId) {
      history.replace(Path.PriceMovementList);
      return;
    }
    try {
      const detail = await fetchMovement({ movementId });
      if (!detail) return;

      const initEffectiveDateTime = detail.effectiveAt ? new Date(detail.effectiveAt) : null;
      setEffectiveDateTime(initEffectiveDateTime);

      const detailMap = {};
      (detail.movementDetails || []).forEach((d) => {
        detailMap[d.productCode] = d;
      });

      const rawRows = PRODUCTS.map((p) => {
        const d = detailMap[p.code];
        if (!d) return { productCode: p.code, movementType: "NO_MOVEMENT", amount: "0.00" };
        const movementType = inferMovementType(d.amount);
        const amount = fmt2(Math.abs(d.amount));
        return { productCode: p.code, movementType, amount };
      });
      const gas97Row = rawRows.find((r) => r.productCode === "gas97");
      const initRows = rawRows.map((r) =>
        (r.productCode === "gas95" || r.productCode === "gas91") && gas97Row
          ? { ...r, movementType: gas97Row.movementType, amount: gas97Row.amount }
          : r
      );
      setProductRows(initRows);

      initialDataRef.current = {
        effectiveDateTime: initEffectiveDateTime,
        productRows: initRows,
      };
    } finally {
      setLoading(false);
    }
  });

  const isGasoline = (code) => code === "gas95" || code === "gas91";

  const handleMovementTypeChange = useCallback((index, value) => {
    setProductRows((prev) =>
      prev.map((row, i) => {
        const shouldSync = i === index || (prev[index].productCode === "gas97" && isGasoline(row.productCode));
        if (!shouldSync) return row;
        return { ...row, movementType: value, amount: value === "NO_MOVEMENT" ? "0.00" : row.amount };
      })
    );
  }, []);

  const handleAmountChange = useCallback((index, value) => {
    setProductRows((prev) =>
      prev.map((row, i) => {
        const shouldSync = i === index || (prev[index].productCode === "gas97" && isGasoline(row.productCode));
        return shouldSync ? { ...row, amount: value } : row;
      })
    );
  }, []);

  const handleAmountBlur = useCallback((index, value) => {
    const clamped = fmt2(Math.min(Math.max(parseFloat(value) || 0, 0), AMOUNT_MAX));
    setProductRows((prev) =>
      prev.map((row, i) => {
        const shouldSync = i === index || (prev[index].productCode === "gas97" && isGasoline(row.productCode));
        return shouldSync ? { ...row, amount: clamped } : row;
      })
    );
  }, []);

  const hasChanges = useMemo(() => {
    const init = initialDataRef.current;
    if (!init) return false;

    if ((effectiveDateTime?.getTime() ?? null) !== (init.effectiveDateTime?.getTime() ?? null))
      return true;

    for (let i = 0; i < productRows.length; i++) {
      if (productRows[i].movementType !== init.productRows[i].movementType) return true;
      if ((productRows[i].amount || "") !== (init.productRows[i].amount || "")) return true;
    }

    return false;
  }, [effectiveDateTime, productRows]);

  const isFormValid = useMemo(() => {
    if (!effectiveDateTime) return false;
    return productRows.every((row) => row.movementType !== null);
  }, [effectiveDateTime, productRows]);

  const handleSaveClick = useCallback(() => {
    if (!hasChanges) return;
    if (!isFormValid) return;
    confirmModal.show();
  }, [hasChanges, isFormValid, confirmModal]);

  const handleConfirmSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const payload = {
        movementId,
        effectiveAt: effectiveDateTime,
        createdBy: movement.createdBy,
        createdByEmail: movement.createdByEmail,
        movementDetails: productRows.map((row) => {
          const isRollback = row.movementType === "ROLLBACK";
          return {
            productCode: row.productCode,
            amount: parseFloat(isRollback ? -row.amount : row.amount) || 0,
          };
        }),
      };
      await submitUpdate(payload);
      confirmModal.close();
      successModal.show();
    } catch {
      // error handled by useApi
    } finally {
      setIsSaving(false);
    }
  }, [
    movementId,
    effectiveDateTime,
    productRows,
    movement,
    submitUpdate,
    confirmModal,
    successModal,
  ]);

  const handleSuccessGotIt = useCallback(() => {
    successModal.close();
    history.push(Path.PriceMovementList);
  }, [successModal, history]);

  const handleCancel = useCallback(() => history.push(Path.PriceMovementList), [history]);

  const SummaryTable = () => {
    const changedRows = productRows.filter((row, i) => {
      const init = initialDataRef.current?.productRows[i];
      if (!init) return false;
      return row.movementType !== init.movementType || (row.amount || "") !== (init.amount || "");
    });

    return (
      <table className={styles.summaryTable}>
        <thead>
          <tr>
            <th>{locale.products}</th>
            <th>{locale.priceMovement}</th>
            <th>{locale.amount}</th>
          </tr>
        </thead>
        <tbody>
          {changedRows.map((row) => {
            const movementLabel =
              MOVEMENT_TYPE_OPTIONS.find((o) => o.value === row.movementType)?.label || "";
            const isIncrease = row.movementType === "INCREASE";
            const isRollback = row.movementType === "ROLLBACK";
            return (
              <tr key={row.productCode}>
                <td>
                  <div className={styles.productCell}>
                    <div style={{ flexShrink: 5 }}>
                      <ProductDot code={row.productCode} />
                    </div>
                    <Text>{prettifyProductCode(row.productCode)}</Text>
                  </div>
                </td>
                <td
                  className={
                    isIncrease ? styles.increase : isRollback ? styles.rollback : styles.noMovement
                  }
                >
                  {movementLabel}
                </td>
                <td
                  className={
                    isIncrease ? styles.increase : isRollback ? styles.rollback : styles.noMovement
                  }
                >
                  ₱{fmt2(row.amount)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const getMovementTypeColor = (movementType) => {
    const isIncrease = movementType === "INCREASE";
    const isRollback = movementType === "ROLLBACK";

    return isIncrease ? "#27ae60" : isRollback ? "#e74c3c" : "#95a2aa";
  };

  if (loading) {
    return (
      <div className={`${styles.container} ${styles.fadeIn}`}>
        <Skeleton variant="text" width={140} height={18} style={{ marginBottom: 24 }} />
        <div className={styles.header}>
          <Skeleton variant="text" width={240} height={40} />
          <div className={styles.headerActions}>
            <Skeleton variant="rect" width={90} height={36} style={{ borderRadius: 6 }} />
            <Skeleton variant="rect" width={70} height={36} style={{ borderRadius: 6 }} />
          </div>
        </div>
        <div className={styles.formSection}>
          <Skeleton variant="rect" width={505} height={40} style={{ borderRadius: 4 }} />
          {[1, 2, 3].map((i) => (
            <div key={i} style={{ display: "flex", gap: 24, alignItems: "center" }}>
              <Skeleton variant="text" width={200} height={18} />
              <Skeleton variant="text" width={160} height={18} />
            </div>
          ))}
        </div>
        <Skeleton variant="rect" width={700} height={400} style={{ borderRadius: 8 }} />
      </div>
    );
  }

  return (
    <div className={`${styles.container} ${styles.fadeIn}`} data-testid="pm-edit-page">
      <ConfirmModal
        {...confirmModal}
        title={locale.saveChangesQuestion}
        content={
          <Text style={{ color: "black", marginLeft: "20px", marginRight: "20px" }}>
            You have made the following changes. Are you sure you want to save?
          </Text>
        }
        primary={{
          text: locale.saveChanges,
          onClick: handleConfirmSave,
          loading: isSaving,
        }}
        secondary={{ text: locale.continueEditing }}
      >
        <SummaryTable />
      </ConfirmModal>
      <SuccessModal
        {...successModal}
        title={locale.success}
        content={locale.detailsHaveBeenSuccessfullyUpdated}
        primary={{ text: locale.gotIt, onClick: handleSuccessGotIt }}
      />

      <BackLink text={locale.priceMovement} path={Path.PriceMovementList} />

      <div className={styles.header}>
        <Title>Edit Price Movement</Title>
        <div className={styles.headerActions}>
          <Button outlined onClick={handleCancel} data-testid="pm-edit-cancel-btn">
            {locale.cancel}
          </Button>
          <Button
            primary
            className={!hasChanges || !isFormValid ? styles.saveBtnInactive : undefined}
            onClick={handleSaveClick}
            data-testid="pm-edit-save-btn"
          >
            {locale.save}
          </Button>
        </div>
      </div>

      <div className={styles.formSection}>
        <Field label="Effective Date and Time" labelPosition={"left"} className={styles.dateField}>
          <DateTimePickerV2
            name="effectiveDate"
            value={effectiveDateTime}
            placeholder="Select date"
            onChange={(name, { value }) => setEffectiveDateTime(value)}
            minDateTime={new Date()}
            data-testid="pm-edit-effective-date"
          />
        </Field>

        <div className={styles.fieldRow}>
          <Text className={styles.fieldLabel}>{locale.processedBy}</Text>
          <Text className={styles.fieldValue}>{processedBy}</Text>
        </div>
        <div className={styles.fieldRow}>
          <Text className={styles.fieldLabel}>{locale.priceMovementType}</Text>
          <Text className={styles.fieldValue}>Nationwide</Text>
        </div>
        <div className={styles.fieldRow}>
          <Text className={styles.fieldLabel}>{locale.priceMovementSubtype}</Text>
          <Text className={styles.fieldValue}>{locale.NA}</Text>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.productsTable}>
          <thead>
            <tr>
              <th>{locale.products}</th>
              <th>{locale.priceMovement}</th>
              <th>{locale.amount}</th>
            </tr>
          </thead>
          <tbody>
            {productRows.map((row, i) => {
              const isNoMovement = row.movementType === "NO_MOVEMENT";
              const isFollower = isGasoline(row.productCode);
              return (
                <tr key={row.productCode} data-testid={`pm-edit-row-${row.productCode}`}>
                  <td>
                    <div className={styles.productCell}>
                      <div style={{ flexShrink: 5 }}>
                        <ProductDot code={row.productCode} />
                      </div>
                      <Text style={{ color: "black" }}>{prettifyProductCode(row.productCode)}</Text>
                    </div>
                  </td>
                  <td>
                    <Autocomplete
                      className={styles.movementSelect}
                      value={row.movementType}
                      options={MOVEMENT_TYPE_OPTIONS}
                      placeholder="Select Price Movement"
                      disabled={isFollower}
                      disableClearable
                      onChange={(_, { value }) => handleMovementTypeChange(i, value)}
                      inputStyle={{
                        color: row.movementType
                          ? getMovementTypeColor(row.movementType)
                          : undefined,
                      }}
                      data-testid={`pm-edit-movement-type-${row.productCode}`}
                    />
                  </td>
                  <td>
                    <div className={styles.amountCell}>
                      <InnerLabelTextfield
                        className={styles.amountInput}
                        value={row.amount}
                        placeholder="0.00"
                        disabled={isNoMovement || isFollower}
                        onChange={(_, { value }) => {
                          if (isNumericPeriod(value) && parseFloat(value || 0) <= AMOUNT_MAX)
                            handleAmountChange(i, value);
                        }}
                        onBlur={(_, { value }) => handleAmountBlur(i, value)}
                        prefix="₱"
                        fontColor={
                          row.movementType ? getMovementTypeColor(row.movementType) : undefined
                        }
                        data-testid={`pm-edit-amount-${row.productCode}`}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PriceMovementEditModule;
